<template>
  <div id="branchEdit">
    <div class="container">

      <div class="page-title">
        <span class="name">{{ branch.name }}</span>
        <button class="save btn btn-primary" @click="save">{{ $t("save") }}</button>
      </div>

      <Tabs>
        <TabSection :title="$t('branch.general')">
          <div class="columns">
            <div class="column">
              <div class="group">
                <label>{{ $t('branch.name') }}</label>
                <input type="text" v-model="branch.name" :class="{ invalid: $validate.error('branch.name') }">
              </div>
              <div class="group">
                <label>{{ $t('branch.locale.title') }}</label>
                <select v-model="branch.locale">
                  <option value="">{{ $t("branch.locale.select") }}</option>
                  <option :value="c.id" v-for="c of supportedLanguages">{{ $t(c.tr) }}</option>
                </select>
              </div>
              <div class="group">
                <label>{{ $t('branch.affiliation') }}</label>
                <select v-model="affiliationSelect">
                  <option :value="null" hidden>{{ $t("choose_select_option") }}</option>
                  <option :value="c.id" v-for="c of affiliations" :key="`aff_${c.id}`">{{ c.name || $t(c.tr) }}</option>
                </select>
                <div class="other group" v-if="affiliationSelect === 'other'">
                  <label>{{ $t('branch.specify') }}</label>
                  <input type="text" v-model="affiliationInput">
                </div>

              </div>
            </div>
            <div class="column">
              <div class="group">
                <ImageUpload identifier="categoryImageUpload" :object="branch.image" @changed="branch.image = $event"
                  :maxSizeInMb="0.8"></ImageUpload>
              </div>
            </div>
          </div>
        </TabSection>

        <TabSection :title="$t('branch.contact.title')" class="contacts">
          <div class="contact" v-for="(contact, contactIndex) in contacts" :key="contactIndex">
            <div class="subtitle">
              <span class="grow">{{ $t("branch.contact.person") }}</span>
              <Tooltip :text="$t('branch.contact.tooltip')" />
              <button class="btn btn-primary" @click="removeContact(contactIndex)">{{ $t('branch.remove') }}</button>
            </div>
            <div class="columns">
              <div class="column">
                <div class="group">
                  <label>{{ $t("branch.type.title") }}</label>
                  <select v-model="contact.contactType"
                    :class="{ invalid: $validate.error('contacts[' + contactIndex + '].contactType') }">
                    <option value="" selected hidden disabled>{{ $t("branch.type.select") }}</option>
                    <option :value="$t('branch.type.distributor')">{{ $t('branch.type.distributor') }}</option>
                    <option :value="$t('branch.type.client')">{{ $t('branch.type.client') }}</option>
                    <option :value="$t('branch.type.billing')">{{ $t('branch.type.billing') }}</option>
                    <option :value="$t('branch.type.other')">{{ $t('branch.type.other') }}</option>
                  </select>
                </div>
                <div class="group">
                  <label>{{ $t('branch.first_name') }}</label>
                  <input type="text" v-model="contact.contactFirstName">
                </div>
                <div class="group">
                  <label>{{ $t('branch.name') }}</label>
                  <input type="text" v-model="contact.contactLastName">
                </div>
                <div class="group">
                  <label>{{ $t('branch.company') }}</label>
                  <input type="text" v-model="contact.companyName">
                </div>
              </div>
              <div class="column">
                <div class="group">
                  <label>{{ $t('branch.email') }}</label>
                  <input type="email" v-model="contact.email"
                    :class="{ invalid: $validate.error('contacts[' + contactIndex + '].email') }">
                </div>
                <div class="group">
                  <label>{{ $t('branch.phone') }}</label>
                  <input type="tel" v-model="contact.phoneNumber"
                    :class="{ invalid: $validate.error('contacts[' + contactIndex + '].phoneNumber') }">
                </div>
                <div class="group">
                  <label>{{ $t('branch.note') }}</label>
                  <textarea v-model="contact.notes" />
                </div>
              </div>
            </div>
          </div>
          <div v-if="contacts.length === 0">
            <div class="empty">{{ $t("branch.contact.empty") }}</div>
          </div>
          <button class="btn btn-primary" @click="addContact">{{ $t('branch.add') }}</button>
        </TabSection>

        <TabSection :title="$t('branch.analytic.title')" class="contacts">
          <TipBox danger v-if="analytics.find(a => a.type === 'google')">
            <span v-html="$t('tips.google_analytics_results')"></span>
          </TipBox>
          <div class="contact" v-for="(analytic, analyticIndex) in analytics" :key="'analytic-' + analyticIndex">
            <div class="subtitle">
              <span class="grow">{{ $t("branch.analytic.service") }}</span>
              <button class="btn btn-primary" @click="removeAnalytic(analyticIndex)">{{ $t('branch.remove') }}</button>
            </div>
            <div class="columns">
              <div class="column">
                <div class="group">
                  <label>{{ $t("branch.type.title") }}</label>
                  <select v-model="analytic.type"
                    :class="{ invalid: $validate.error('analytics[' + analyticIndex + '].type') }">
                    <option value="" selected hidden disabled>{{ $t("branch.type.select") }}</option>
                    <option value="pixel">Facebook Pixel</option>
                    <option value="google">Google Analytics</option>
                  </select>
                </div>
              </div>
              <div class="column">
                <div class="group">
                  <label>{{ $t('branch.analytic.id') }}</label>
                  <input type="email" v-model="analytic.id"
                    :class="{ invalid: $validate.error('analytics[' + analyticIndex + '].id') }">
                </div>
              </div>
            </div>
          </div>
          <div v-if="analytics.length === 0">
            <div class="empty">{{ $t("branch.analytic.empty") }}</div>
          </div>
          <button class="btn btn-primary" @click="addAnalytic">{{ $t('branch.add') }}</button>
        </TabSection>
      </Tabs>

      <div class="footer-actions">
        <button class="btn btn-primary" @click="save">{{ $t("save") }}</button>
      </div>

    </div>
  </div>
</template>

<script>
  import { mapActions } from "vuex";
  import ImageUpload from "@/components/tools/ImageUpload.vue";

  export default {
    name: 'branchEdit',

    components: { ImageUpload },

    data() {
      return {
        branchId: 0,
        editMode: false,
        augment: 'backend',
        supportedLanguages: [
          { id: 'en', tr: 'translation.en' },
          { id: 'fr', tr: 'translation.fr' },
        ],
        affiliations: [
          { id: 'ishopfood', name: 'iShopFood' },
          { id: 'payfacto', name: 'Payfacto' },
          { id: 'other', tr: 'other' },
        ],
        branch: {
          id: null,
          name: '',
          locale: 'en',
          affiliation: null,
          image: {
            url: ''
          }
        },
        contacts: [],
        analytics: [],
        imageFile: "",
      }
    },

    computed: {
      affiliationSelect: {
        get() {
          return this.branch.affiliation ? (this.branch.affiliation.split(':')[0] || null) : null;
        },
        set(val) {
          this.branch.affiliation = val;
        }
      },
      affiliationInput: {
        get() {
          return this.branch.affiliation ? (this.branch.affiliation.split(':')[1] || null) : null;
        },
        set(val) {
          this.branch.affiliation = val ? `${this.affiliationSelect}:${val}` : this.affiliationSelect;
        }
      }
    },

    validate: {
      "branch.name": {
        required: true
      },
      contacts: {
        array: true,
        elements: {
          contactType: {
            required: true
          },
          phoneNumber: {
            phone: true
          },
          email: {
            email: true
          }
        }
      },
      analytics: {
        array: true,
        elements: {
          type: {
            required: true
          },
          id: {
            required: true
          }
        }
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "branch.title";
      this.$store.state.pageIcon = "/icon/branches.svg";
      this.branchId = this.$route.query.branch;

      if (this.branchId) {
        showSpinner();
        await this.loadBranch();
        await this.loadContacts();
        hideSpinner();
      }
    },

    methods: {

      ...mapActions(["getBranch", "getBranchContacts", "createBranch", "saveBranch", "saveBranchContacts"]),

      async loadBranch() {
        let response = await this.getBranch({ branchId: this.branchId, augment: this.augment, fallback: false });
        if (response) {
          this.branch = response;
          this.analytics = this.branch.analytics;
        } else {
          console.log("erreur de fetch branch", response);
          toast(this.$t("error.no_server"), "error");
        }
      },

      async loadContacts() {
        let response = await this.getBranchContacts({ branchId: this.branchId });
        if (response) {
          this.contacts = response;
        } else {
          console.log("erreur de fetch contacts");
        }
      },

      async save() {
        if (!this.$validate.valid()) {
          toast(this.$t("branch.error.required"), "warning");
          this.$validate.validateAllOnce();
          return;
        }
        this.$store.commit("showSpinner", true);

        let branchResponse = null;
        let contactResponse = null;
        if (this.branchId) {
          branchResponse = await this.saveBranchAction();
          if (!branchResponse) {
            this.handleError("error save branch", branchResponse);
            return;
          }
          contactResponse = await this.saveContacts();
          if (!contactResponse) {
            this.handleError("error save contacts", contactResponse);
            return;
          }
        } else {
          branchResponse = await this.createBranchAction();
          if (!branchResponse) {
            this.handleError("error create branch", branchResponse);
            return;
          }
          this.branchId = branchResponse.id;
          contactResponse = await this.saveContacts();
          if (!contactResponse) {
            this.handleError("error save contacts", contactResponse);
            return;
          }
          if (!this.$route.query.branch) {
            this.$router.push("/branch/edit?branch=" + this.branchId);
          }
        }

        toast(this.$t("branch.success"), "success");
        this.$store.commit("showSpinner", false);
        this.$store.commit("updateContextBranch", branchResponse);
      },

      async createBranchAction() {
        let data = {
          name: this.branch.name,
          locale: this.branch.locale,
          affiliation: this.branch.affiliation,
          analytics: this.analytics,
          image: getImageData(this.branch.image)
        };
        let response = await this.createBranch({ augment: this.augment, data });
        if (response) {
          this.branchId = data.id;
          this.branch = data;
        }
        return response;
      },

      async saveBranchAction() {
        let data = {
          name: this.branch.name,
          locale: this.branch.locale,
          affiliation: this.branch.affiliation,
          analytics: this.analytics,
          image: getImageData(this.branch.image),
        };
        return await this.saveBranch({ branchId: this.branchId, augment: this.augment, data });
      },

      async saveContacts() {
        let response = await this.saveBranchContacts({ branchId: this.branchId, contacts: this.contacts });
        if (response) {
          this.contacts = response;
        }
        return response;
      },

      handleError(details, error) {
        console.log("error", details, error);
        this.$store.commit("showSpinner", false);
        toast(this.$t("error.no_server"), "error");
      },

      addContact() {
        this.contacts.push({
          contactType: "",
          contactLastName: "",
          contactFirstName: "",
          companyName: "",
          phoneNumber: "",
          email: "",
          notes: ""
        });
      },

      addAnalytic() {
        this.analytics.push({
          type: "",
          id: ""
        })
      },

      removeContact(index) {
        this.contacts.splice(index, 1);
      },

      removeAnalytic(index) {
        this.analytics.splice(index, 1);
      }
    }

  };
</script>

<style lang="scss" scoped>
  #branchEdit {
    display: flex;
    flex-direction: column;
  }

  .empty {
    margin-bottom: 30px;
    color: #808080;
  }

  .subtitle .btn {
    margin-left: 20px;
  }

  .grow {
    flex-grow: 1;
  }

  .contact:not(:last-child) {
    margin-bottom: 20px;
  }

  textarea {
    height: 6.4em;
  }

  .other {
    margin-top: 15px;
  }
</style>